import {
  type Context,
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';

import { cloneDeep } from 'lodash';

import { useGlobalContext } from './GlobalContext';

export const enum PortalComponent {
  ADD_ADMIN_EPISODE_ACCORDION,
  ADD_EDIT_SURVEY_RESPONDENT_ACCORDION,
  ADD_GROUP_CLASS_ACCORDION,
  ADD_PATIENT_ACCORDION,
  ADD_TASK_ACCORDION,
  EDIT_ADMISSIONS_TEAM,
  EDIT_CARE_TEAM,
  EDIT_GROUP_CLASS_ACCORDION,
  EDIT_ABOUTME_ACCORDION,
  EDIT_PERMISSIONS_ACCORDION,
  ERROR_TOAST,
  NOTE_ACCORDION,
  OUT_OF_OFFICE_ACCORDION,
  PROVIDER_ASSIGNMENT_MODAL,
  SAVE_AVAILABILITY,
  SAVE_SETTINGS,
  SURVEY_ACCORDION,
  TOAST_ACCORDION,
  BILLING_PERIOD_ACCORDION,
  ADD_EDIT_PROFILE_ACCORDION,
  TREATMENT_EPISODE_ACCORDION,
  ADD_REASSIGNMENT_MODAL,
  CREATE_INQUIRY_DRAWER,
  INQUIRY_DETAIL_DRAWER,
}

export type ComponentState = {
  data?: any;
  hasUnsavedChanges?: boolean;
  isExpanded?: boolean;
  isFullScreen?: boolean;
  isVisible?: boolean;
  showLinkedPatient?: boolean;
};

type ComponentVisibilityAction = {
  component: PortalComponent;
  data?: any;
  hasUnsavedChanges?: boolean;
  isExpanded?: boolean;
  isFullScreen?: boolean;
  isVisible?: boolean;
  showLinkedPatient?: boolean;
};

export type SetComponentVisibilityAction = (
  action: ComponentVisibilityAction,
) => void;

type ComponentVisibility = {
  [PortalComponent.ADD_REASSIGNMENT_MODAL]: ComponentState;
  [PortalComponent.ADD_ADMIN_EPISODE_ACCORDION]: ComponentState;
  [PortalComponent.ADD_EDIT_SURVEY_RESPONDENT_ACCORDION]: ComponentState;
  [PortalComponent.ADD_GROUP_CLASS_ACCORDION]: ComponentState;
  [PortalComponent.ADD_PATIENT_ACCORDION]: ComponentState;
  [PortalComponent.ADD_TASK_ACCORDION]: ComponentState;
  [PortalComponent.EDIT_ADMISSIONS_TEAM]: ComponentState;
  [PortalComponent.EDIT_CARE_TEAM]: ComponentState;
  [PortalComponent.EDIT_GROUP_CLASS_ACCORDION]: ComponentState;
  [PortalComponent.EDIT_ABOUTME_ACCORDION]: ComponentState;
  [PortalComponent.EDIT_PERMISSIONS_ACCORDION]: ComponentState;
  [PortalComponent.ADD_EDIT_PROFILE_ACCORDION]: ComponentState;
  [PortalComponent.ERROR_TOAST]: ComponentState;
  [PortalComponent.NOTE_ACCORDION]: ComponentState;
  [PortalComponent.OUT_OF_OFFICE_ACCORDION]: ComponentState;
  [PortalComponent.PROVIDER_ASSIGNMENT_MODAL]: ComponentState;
  [PortalComponent.SAVE_AVAILABILITY]: ComponentState;
  [PortalComponent.SAVE_SETTINGS]: ComponentState;
  [PortalComponent.SURVEY_ACCORDION]: ComponentState;
  [PortalComponent.TOAST_ACCORDION]: ComponentState;
  [PortalComponent.BILLING_PERIOD_ACCORDION]: ComponentState;
  [PortalComponent.CREATE_INQUIRY_DRAWER]: ComponentState;
  [PortalComponent.INQUIRY_DETAIL_DRAWER]: ComponentState;
  [PortalComponent.TREATMENT_EPISODE_ACCORDION]: ComponentState;
};

type ComponentVisibilityReducer = (
  state: ComponentVisibility,
  action: ComponentVisibilityAction,
) => ComponentVisibility;

export type TComponentPortalContext = {
  addReassignmentModal: ComponentState;
  addEditSurveyRespondentAccordion: ComponentState;
  addGroupClassAccordion: ComponentState;
  addTaskAccordion: ComponentState;
  createInquiryDrawer: ComponentState;
  editProfileAccordion: ComponentState;
  editAdmissionsTeam: ComponentState;
  editProfileAboutMeAccordion: ComponentState;
  editPermissionsAccordion: ComponentState;
  editCareTeam: ComponentState;
  editGroupClassAccordion: ComponentState;
  errorToast: ComponentState;
  inquiryDetailDrawer: ComponentState;
  noteAccordion: ComponentState;
  outOfOfficeAccordion: ComponentState;
  patientHasUnsavedChanges: boolean;
  providerAssignmentModal: ComponentState;
  saveAvailability: ComponentState;
  saveSettings: ComponentState;
  setComponentVisibility: SetComponentVisibilityAction;
  surveyAccordion: ComponentState;
  toastAccordion: ComponentState;
  billingPeriodAccordion: ComponentState;
  treatmentEpisodeAccordion: ComponentState;
};

const defaultComponentState: ComponentState = {
  data: null,
  hasUnsavedChanges: false,
  isExpanded: false,
  isFullScreen: false,
  isVisible: false,
  showLinkedPatient: false,
};

export const ComponentPortalContext: Context<TComponentPortalContext> =
  createContext<TComponentPortalContext>({
    addReassignmentModal: cloneDeep(defaultComponentState),
    addEditSurveyRespondentAccordion: cloneDeep(defaultComponentState),
    addGroupClassAccordion: cloneDeep(defaultComponentState),
    addTaskAccordion: cloneDeep(defaultComponentState),
    billingPeriodAccordion: cloneDeep(defaultComponentState),
    createInquiryDrawer: cloneDeep(defaultComponentState),
    editAdmissionsTeam: cloneDeep(defaultComponentState),
    editCareTeam: cloneDeep(defaultComponentState),
    editGroupClassAccordion: cloneDeep(defaultComponentState),
    editPermissionsAccordion: cloneDeep(defaultComponentState),
    editProfileAboutMeAccordion: cloneDeep(defaultComponentState),
    editProfileAccordion: cloneDeep(defaultComponentState),
    errorToast: cloneDeep(defaultComponentState),
    inquiryDetailDrawer: cloneDeep(defaultComponentState),
    noteAccordion: cloneDeep(defaultComponentState),
    outOfOfficeAccordion: cloneDeep(defaultComponentState),
    patientHasUnsavedChanges: false,
    providerAssignmentModal: cloneDeep(defaultComponentState),
    saveAvailability: cloneDeep(defaultComponentState),
    saveSettings: cloneDeep(defaultComponentState),
    setComponentVisibility: () => {},
    surveyAccordion: cloneDeep(defaultComponentState),
    toastAccordion: cloneDeep(defaultComponentState),
    treatmentEpisodeAccordion: cloneDeep(defaultComponentState),
  });

const componentVisibilityReducer: ComponentVisibilityReducer = (
  state: ComponentVisibility,
  action: ComponentVisibilityAction,
) => {
  const {
    component,
    data,
    hasUnsavedChanges,
    isExpanded,
    isFullScreen,
    isVisible,
    showLinkedPatient,
  } = action;

  const {
    data: currentData,
    hasUnsavedChanges: currentHasUnsavedChanges,
    isExpanded: currentExpandedState,
    isFullScreen: currentIsFullScreen,
    isVisible: currentVisibleState,
    showLinkedPatient: currentIsShowLinkedPatientState,
  } = state[component];

  const stateCopy = { ...state };

  // if an accordion is newly expanded, then collapse all accordion
  if (isExpanded && !currentExpandedState) {
    Object.keys(stateCopy).forEach((key) => {
      stateCopy[key].isExpanded = false;
    });
  }

  return {
    ...stateCopy,
    [component]: {
      data: data ?? currentData,
      hasUnsavedChanges: hasUnsavedChanges ?? currentHasUnsavedChanges,
      isExpanded: isExpanded ?? currentExpandedState,
      isFullScreen: isFullScreen ?? currentIsFullScreen,
      isVisible: isVisible ?? currentVisibleState,
      showLinkedPatient: showLinkedPatient ?? currentIsShowLinkedPatientState,
    },
  };
};

const defaultVisibilityState: ComponentVisibility = {
  [PortalComponent.ADD_REASSIGNMENT_MODAL]: defaultComponentState,
  [PortalComponent.ADD_ADMIN_EPISODE_ACCORDION]: defaultComponentState,
  [PortalComponent.ADD_EDIT_SURVEY_RESPONDENT_ACCORDION]: defaultComponentState,
  [PortalComponent.ADD_GROUP_CLASS_ACCORDION]: defaultComponentState,
  [PortalComponent.ADD_PATIENT_ACCORDION]: defaultComponentState,
  [PortalComponent.ADD_TASK_ACCORDION]: defaultComponentState,
  [PortalComponent.EDIT_ADMISSIONS_TEAM]: defaultComponentState,
  [PortalComponent.EDIT_CARE_TEAM]: defaultComponentState,
  [PortalComponent.EDIT_GROUP_CLASS_ACCORDION]: defaultComponentState,
  [PortalComponent.EDIT_ABOUTME_ACCORDION]: defaultComponentState,
  [PortalComponent.EDIT_PERMISSIONS_ACCORDION]: defaultComponentState,
  [PortalComponent.ADD_EDIT_PROFILE_ACCORDION]: defaultComponentState,
  [PortalComponent.ERROR_TOAST]: defaultComponentState,
  [PortalComponent.NOTE_ACCORDION]: defaultComponentState,
  [PortalComponent.OUT_OF_OFFICE_ACCORDION]: defaultComponentState,
  [PortalComponent.PROVIDER_ASSIGNMENT_MODAL]: defaultComponentState,
  [PortalComponent.SAVE_AVAILABILITY]: defaultComponentState,
  [PortalComponent.SAVE_SETTINGS]: defaultComponentState,
  [PortalComponent.SURVEY_ACCORDION]: defaultComponentState,
  [PortalComponent.TOAST_ACCORDION]: defaultComponentState,
  [PortalComponent.BILLING_PERIOD_ACCORDION]: defaultComponentState,
  [PortalComponent.CREATE_INQUIRY_DRAWER]: defaultComponentState,
  [PortalComponent.INQUIRY_DETAIL_DRAWER]: defaultComponentState,
  [PortalComponent.TREATMENT_EPISODE_ACCORDION]: defaultComponentState,
};

export const ComponentPortalContextProvider = ({
  children,
}: PropsWithChildren) => {
  const [visibility, dispatch] = useReducer<ComponentVisibilityReducer>(
    componentVisibilityReducer,
    defaultVisibilityState,
  );

  const setComponentVisibility = useCallback<SetComponentVisibilityAction>(
    ({
      component,
      data,
      hasUnsavedChanges,
      isExpanded,
      isFullScreen,
      isVisible,
      showLinkedPatient,
    }) => {
      dispatch({
        component,
        data,
        hasUnsavedChanges,
        isExpanded,
        isFullScreen,
        isVisible,
        showLinkedPatient,
      });
    },
    [dispatch],
  );

  const patientHasUnsavedChanges = useMemo<boolean>(
    () =>
      visibility[PortalComponent.ADD_ADMIN_EPISODE_ACCORDION]
        .hasUnsavedChanges ||
      visibility[PortalComponent.ADD_EDIT_SURVEY_RESPONDENT_ACCORDION]
        .hasUnsavedChanges ||
      visibility[PortalComponent.EDIT_ADMISSIONS_TEAM].hasUnsavedChanges ||
      visibility[PortalComponent.EDIT_CARE_TEAM].hasUnsavedChanges ||
      visibility[PortalComponent.NOTE_ACCORDION].hasUnsavedChanges ||
      visibility[PortalComponent.SURVEY_ACCORDION].hasUnsavedChanges,
    [
      visibility[PortalComponent.ADD_ADMIN_EPISODE_ACCORDION].hasUnsavedChanges,
      visibility[PortalComponent.ADD_EDIT_SURVEY_RESPONDENT_ACCORDION]
        .hasUnsavedChanges,
      visibility[PortalComponent.EDIT_CARE_TEAM].hasUnsavedChanges,
      visibility[PortalComponent.NOTE_ACCORDION].hasUnsavedChanges,
      visibility[PortalComponent.SURVEY_ACCORDION].hasUnsavedChanges,
    ],
  );

  const { currentPatientId } = useGlobalContext();

  useEffect(() => {
    setComponentVisibility({
      component: PortalComponent.ADD_ADMIN_EPISODE_ACCORDION,
      hasUnsavedChanges: false,
      isExpanded: false,
      isVisible: false,
    });
    setComponentVisibility({
      component: PortalComponent.ADD_EDIT_SURVEY_RESPONDENT_ACCORDION,
      data: {},
      hasUnsavedChanges: false,
      isExpanded: false,
      isVisible: false,
    });
    setComponentVisibility({
      component: PortalComponent.NOTE_ACCORDION,
      hasUnsavedChanges: false,
      isExpanded: false,
      isVisible: false,
    });
    setComponentVisibility({
      component: PortalComponent.SURVEY_ACCORDION,
      hasUnsavedChanges: false,
      isExpanded: false,
      isFullScreen: false,
      isVisible: false,
    });
  }, [currentPatientId]);

  return (
    <ComponentPortalContext.Provider
      value={{
        addReassignmentModal:
          visibility[PortalComponent.ADD_REASSIGNMENT_MODAL],
        addEditSurveyRespondentAccordion:
          visibility[PortalComponent.ADD_EDIT_SURVEY_RESPONDENT_ACCORDION],
        addGroupClassAccordion:
          visibility[PortalComponent.ADD_GROUP_CLASS_ACCORDION],
        addTaskAccordion: visibility[PortalComponent.ADD_TASK_ACCORDION],
        billingPeriodAccordion:
          visibility[PortalComponent.BILLING_PERIOD_ACCORDION],
        createInquiryDrawer: visibility[PortalComponent.CREATE_INQUIRY_DRAWER],
        editAdmissionsTeam: visibility[PortalComponent.EDIT_ADMISSIONS_TEAM],
        editCareTeam: visibility[PortalComponent.EDIT_CARE_TEAM],
        editGroupClassAccordion:
          visibility[PortalComponent.EDIT_GROUP_CLASS_ACCORDION],
        editPermissionsAccordion:
          visibility[PortalComponent.EDIT_PERMISSIONS_ACCORDION],
        editProfileAboutMeAccordion:
          visibility[PortalComponent.EDIT_ABOUTME_ACCORDION],
        editProfileAccordion:
          visibility[PortalComponent.ADD_EDIT_PROFILE_ACCORDION],
        errorToast: visibility[PortalComponent.ERROR_TOAST],
        inquiryDetailDrawer: visibility[PortalComponent.INQUIRY_DETAIL_DRAWER],
        noteAccordion: visibility[PortalComponent.NOTE_ACCORDION],
        outOfOfficeAccordion:
          visibility[PortalComponent.OUT_OF_OFFICE_ACCORDION],
        patientHasUnsavedChanges,
        providerAssignmentModal:
          visibility[PortalComponent.PROVIDER_ASSIGNMENT_MODAL],
        saveAvailability: visibility[PortalComponent.SAVE_AVAILABILITY],
        saveSettings: visibility[PortalComponent.SAVE_SETTINGS],
        setComponentVisibility,
        surveyAccordion: visibility[PortalComponent.SURVEY_ACCORDION],
        toastAccordion: visibility[PortalComponent.TOAST_ACCORDION],
        treatmentEpisodeAccordion:
          visibility[PortalComponent.TREATMENT_EPISODE_ACCORDION],
      }}
    >
      {children}
    </ComponentPortalContext.Provider>
  );
};

export const useComponentPortalContext = (): TComponentPortalContext => {
  const context = useContext(ComponentPortalContext);
  if (!context) {
    throw new Error(
      'useComponentPortalContext must be used within a ComponentPortalContextProvider.',
    );
  }
  return context;
};
